import { trigger, transition, style, animate } from '@angular/animations';
import { HttpClient } from '@angular/common/http';

import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { DashboardService } from 'src/app/shared/service/dashboard/dashboard.service';

interface ChatMessage {
  role: 'user' | 'model';
  content: string;
  file?: {
    name: string;
    type?: string;
  };
  displayedContent?: string;
  isTyping?: boolean;

  displayType?: string;

  tableData?: any[];
  displayedColumns?: string[];

  amountSummary?: {
    min: number;
    max: number;
    average: number;
  } | null;

  responseTimeText?: string;
  // =========================================================
  // CURRENCY CONVERTER
  // =========================================================
  currencyAmount?: number;
  fromCurrency?: string;
  toCurrency?: string;
  currencyResult?: number;
}

@Component({
  selector: 'app-meraki-ai',
  templateUrl: './meraki-ai.component.html',
  styleUrls: ['./meraki-ai.component.scss'],

  animations: [
    trigger('messageAnimation', [
      transition('* => *', [
        style({
          opacity: 0,
          transform: 'translateY(15px)',
        }),

        animate(
          '500ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          }),
        ),
      ]),
    ]),

    trigger('chatMessageAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(12px)',
        }),

        animate(
          '350ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          }),
        ),
      ]),
    ]),
  ],
})
export class MerakiAiComponent implements OnInit, OnDestroy {
  // =========================================================
  // CURRENCY CONVERTER
  // =========================================================

  public currencyRates: { [key: string]: number } = { USD: 1 };
  public isFetchingRates: boolean = false;
  // =========================================================
  // CHAT HISTORY
  // =========================================================

  chatMessages: ChatMessage[] = [];

  private readonly SESSION_KEY = 'meraki_ai_chat_history';

  // =========================================================
  // PROMPT
  // =========================================================

  promptText = '';

  question: any = '';

  // =========================================================
  // FILE
  // =========================================================

  selectedFile: any = null;

  isDragOver = false;

  // =========================================================
  // PROCESSING
  // =========================================================

  isProcessing = false;

  isSlowResponse = false;

  messageAnimationKey = 0;

  processingMessages: string[] = [
    'Understanding your request...',
    'Analyzing the provided information...',
    'Searching through the available data...',
    'Processing the relevant records...',
    'Connecting the dots...',
    'Validating the results...',
    'Preparing the best possible answer...',
    'Almost there...',
  ];

  currentMessageIndex = 0;

  currentProcessingMessage = this.processingMessages[0];

  private processingMessageTimer: any;

  // =========================================================
  // CURRENT RESPONSE
  // =========================================================

  responseText = '';

  displayType = '';

  tableData: any[] = [];

  displayedColumns: string[] = [];

  amountSummary: any = null;

  responseTime = 0;

  responseTimeText = '';

  requestStartTime = 0;

  isTableExpanded = false;

  // =========================================================
  // SUGGESTIONS
  // =========================================================

  selectedSuggestions: string[] = [];

  allSuggestions = [
    'What is the Agency Fee of',
    'What is the towage cost for',
    'What is the pilotage charge for',
    'What are the port charges for',
    'What is the berth hire for',
    'What is the fresh water cost for',
    'What is the launch boat charge for',
    'What is the total PDA amount for',
    'Show FDA for',
    'Show PDA for',
    'Show Final DA for',
    'List all disbursements for',
  ];
  // =========================================================
  // CURRENCY CONVERTER
  // =========================================================

  currencies = [
    {
      code: 'USD',
      name: 'US Dollar',
    },
    {
      code: 'EUR',
      name: 'Euro',
    },
    {
      code: 'GBP',
      name: 'British Pound',
    },
    {
      code: 'INR',
      name: 'Indian Rupee',
    },
    {
      code: 'SGD',
      name: 'Singapore Dollar',
    },
    {
      code: 'AED',
      name: 'UAE Dirham',
    },
  ];
  showSuggestions = true;

  filteredSuggestions: string[] = [...this.allSuggestions];

  // =========================================================
  // VOICE
  // =========================================================

  isListening = false;

  private recognition: any;

  private originalPromptText = '';

  private finalVoiceText = '';

  private interimVoiceText = '';

  // =========================================================
  // TYPING
  // =========================================================

  private typingTimer: any;

  // =========================================================
  // VIEW
  // =========================================================

  @ViewChild('fileInput')
  fileInput!: ElementRef<HTMLInputElement>;

  @ViewChild('chatMessagesContainer')
  chatMessagesContainer!: ElementRef<HTMLDivElement>;

  // =========================================================
  // CONSTRUCTOR
  // =========================================================

  constructor(
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private http: HttpClient,
  ) {}

  // =========================================================
  // INIT
  // =========================================================

  ngOnInit(): void {
    this.restoreChatHistory();
    this.fetchLiveExchangeRates();
  }

  fetchLiveExchangeRates(): void {
    this.isFetchingRates = true;

    this.dashboardService.getExchangeRates().subscribe({
      next: (data) => {
        if (data && data.status === 'success' && data.rates) {
          this.currencyRates = data.rates;
        }

        this.isFetchingRates = false;
      },
      error: (err) => {
        console.warn(
          'Failed to fetch live currency rates, falling back to cached rates.',
          err,
        );
        this.isFetchingRates = false;
      },
    });
  }

  // =========================================================
  // DESTROY
  // =========================================================

  ngOnDestroy(): void {
    this.stopProcessingMessages();

    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }

    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.log(error);
      }
    }
  }

  // =========================================================
  // SESSION STORAGE
  // =========================================================

  private saveChatHistory(): void {
    try {
      // Clean up internal animation variables before persisting
      const historyToSave = this.chatMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        displayType: msg.displayType,
        tableData: msg.tableData,
        displayedColumns: msg.displayedColumns,
        amountSummary: msg.amountSummary,
        responseTimeText: msg.responseTimeText,
      }));

      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(historyToSave));
    } catch (error) {
      console.error('Unable to save Meraki AI chat history:', error);
    }
  }

  private restoreChatHistory(): void {
    try {
      const storedHistory = sessionStorage.getItem(this.SESSION_KEY);

      if (!storedHistory) {
        return;
      }

      const parsedHistory = JSON.parse(storedHistory);

      if (Array.isArray(parsedHistory) && parsedHistory.length) {
        // Ensure static text is displayed without re-typing on load
        this.chatMessages = parsedHistory.map((msg: ChatMessage) => ({
          ...msg,
          displayedContent: msg.content,
          isTyping: false,
        }));

        this.showSuggestions = false;

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      }
    } catch (error) {
      console.error('Unable to restore Meraki AI chat history:', error);

      sessionStorage.removeItem(this.SESSION_KEY);
    }
  }

  // =========================================================
  // NEW CHAT
  // =========================================================

  newChat(): void {
    this.stopProcessingMessages();

    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }

    if (this.isListening && this.recognition) {
      try {
        this.recognition.stop();
      } catch (error) {
        console.log(error);
      }
    }

    this.chatMessages = [];

    sessionStorage.removeItem(this.SESSION_KEY);

    this.promptText = '';

    this.question = '';

    this.selectedFile = null;

    this.responseText = '';

    this.displayType = '';

    this.tableData = [];

    this.displayedColumns = [];

    this.amountSummary = null;

    this.responseTime = 0;

    this.responseTimeText = '';

    this.isProcessing = false;

    this.isSlowResponse = false;

    this.isTableExpanded = false;

    this.isListening = false;

    this.finalVoiceText = '';

    this.interimVoiceText = '';

    this.originalPromptText = '';

    this.selectedSuggestions = [];

    this.showSuggestions = true;

    this.showAllSuggestions();

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  // =========================================================
  // FILE SELECT
  // =========================================================

  onFileSelected(event: any): void {
    const files = event.target.files;

    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  // =========================================================
  // DRAG
  // =========================================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();

    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();

    this.isDragOver = false;

    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  // =========================================================
  // REMOVE FILE
  // =========================================================

  removeSelectedFile(event?: Event): void {
    event?.stopPropagation();

    this.selectedFile = null;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // =========================================================
  // SUBMIT
  // =========================================================

  onSubmit(): void {
    const prompt = this.promptText.trim();

    if (!prompt && !this.selectedFile) {
      return;
    }

    // -------------------------------------------------------
    // HISTORY BEFORE CURRENT QUESTION
    // -------------------------------------------------------

    const history = this.chatMessages.map((message) => ({
      role: message.role,
      content: message.content,
    }));
    // Capture file before clearing
    const currentFile = this.selectedFile ? {
      name: this.selectedFile.name,
      type: this.selectedFile.type
    } : undefined;
    
    // -------------------------------------------------------
    // ADD USER MESSAGE IMMEDIATELY
    // -------------------------------------------------------

    const userMessage: ChatMessage = {
      role: 'user',
      content: prompt,
      file: currentFile
    };

    this.chatMessages.push(userMessage);

    this.saveChatHistory();

    this.showSuggestions = false;

    // -------------------------------------------------------
    // RESET RESPONSE
    // -------------------------------------------------------

    this.responseText = '';

    this.displayType = '';

    this.tableData = [];

    this.displayedColumns = [];

    this.amountSummary = null;

    this.responseTime = 0;

    this.responseTimeText = '';

    this.isTableExpanded = false;

    // -------------------------------------------------------
    // PROCESSING
    // -------------------------------------------------------

    this.isProcessing = true;

    this.isSlowResponse = false;

    this.startProcessingMessages();

    this.requestStartTime = performance.now();

    // Save prompt before clearing
    const currentPrompt = prompt;
    const fileToUpload = this.selectedFile;
    // Clear input immediately
    this.promptText = '';
    this.selectedFile = null;
    this.scrollToBottom();

    // -------------------------------------------------------
    // API
    // -------------------------------------------------------

    this.dashboardService
      .generateAIResponse(currentPrompt, fileToUpload, history)
      .subscribe({
        next: (response: any) => {
          this.stopProcessingMessages();

          this.responseTime = performance.now() - this.requestStartTime;

          this.responseTimeText = this.formatResponseTime(this.responseTime);

          const responseType = response?.display_type || '';

          const statement = response?.statement || '';

          // -------------------------------------------------
          // CREATE AI MESSAGE
          // -------------------------------------------------

          const aiMessage: ChatMessage = {
            role: 'model',
            content: statement,
            displayedContent: responseType === 'table' ? statement : '',
            isTyping: responseType !== 'table',
            displayType: responseType,
            responseTimeText: this.responseTimeText,
            currencyAmount: undefined,
            fromCurrency: 'USD',
            toCurrency: 'INR',
            currencyResult: undefined,
          };

          // -------------------------------------------------
          // TABLE
          // -------------------------------------------------

          if (responseType === 'table') {
            this.sortColumn = '';
            this.sortDirection = 'asc';
            const data = response?.data || [];

            aiMessage.tableData = data;

            aiMessage.displayedColumns =
              data.length > 0 ? Object.keys(data[0]) : [];

            aiMessage.amountSummary = this.calculateAmountSummaryForData(data);

            this.tableData = data;

            this.displayedColumns = aiMessage.displayedColumns;

            this.amountSummary = aiMessage.amountSummary;
          }

          // -------------------------------------------------
          // PUSH AI MESSAGE
          // -------------------------------------------------

          this.chatMessages.push(aiMessage);

          // Save complete conversation
          this.saveChatHistory();

          // -------------------------------------------------
          // CURRENT RESPONSE STATE
          // -------------------------------------------------

          this.displayType = responseType;

          this.question = currentPrompt;

          this.selectedFile = null;

          if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
          }

          // -------------------------------------------------
          // STOP PROCESSING & TRIGGER TYPING
          // -------------------------------------------------

          setTimeout(() => {
            this.isProcessing = false;

            this.scrollToBottom();

            // For text response animate typing
            if (responseType !== 'table') {
              const targetMessage =
                this.chatMessages[this.chatMessages.length - 1];
              this.typeResponse(statement, targetMessage);
            }
          }, 600);
        },

        error: () => {
          this.stopProcessingMessages();

          this.isProcessing = false;

          this.isSlowResponse = false;

          // Remove the user message because
          // the request failed
          this.chatMessages = this.chatMessages.filter(
            (_, index) => index !== this.chatMessages.length - 1,
          );

          this.saveChatHistory();

          this.toastr.error('Unable to generate AI response.', 'Error');
        },
      });
  }

  // =========================================================
  // PROCESSING MESSAGE ROTATION
  // =========================================================

  startProcessingMessages(): void {
    this.currentMessageIndex = 0;

    this.currentProcessingMessage =
      this.processingMessages[this.currentMessageIndex];

    this.messageAnimationKey = 0;

    this.stopProcessingMessages();

    this.processingMessageTimer = setInterval(() => {
      this.currentMessageIndex =
        (this.currentMessageIndex + 1) % this.processingMessages.length;

      this.currentProcessingMessage =
        this.processingMessages[this.currentMessageIndex];

      this.messageAnimationKey++;
    }, 3000);
  }

  stopProcessingMessages(): void {
    if (this.processingMessageTimer) {
      clearInterval(this.processingMessageTimer);

      this.processingMessageTimer = null;
    }
  }
  isAmountColumn(column: string): boolean {
    const normalizedColumn = column.toLowerCase();

    return (
      normalizedColumn.includes('amount') ||
      normalizedColumn.includes('cost') ||
      normalizedColumn.includes('charge') ||
      normalizedColumn.includes('fee')
    );
  }
  formatIndianAmount(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return value;
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  // =========================================================
  // SCROLL
  // =========================================================

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.chatMessagesContainer?.nativeElement;

      if (element) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 50);
  }

  // =========================================================
  // RESPONSE TIME
  // =========================================================

  formatResponseTime(ms: number): string {
    if (ms < 1000) {
      return `${Math.round(ms)} ms`;
    }

    return `${(ms / 1000).toFixed(2)} sec`;
  }

  // =========================================================
  // TABLE SUMMARY
  // =========================================================

  calculateAmountSummaryForData(data: any[]): {
    min: number;
    max: number;
    average: number;
  } | null {
    const amounts = data
      .map((row) => Number(row['fda_amount (USD)']))
      .filter((value) => !isNaN(value));

    if (!amounts.length) {
      return null;
    }

    const min = Math.min(...amounts);

    const max = Math.max(...amounts);

    const average =
      amounts.reduce((sum, value) => sum + value, 0) / amounts.length;

    return {
      min,
      max,
      average,
    };
  }

  calculateAmountSummary(): void {
    this.amountSummary = this.calculateAmountSummaryForData(this.tableData);
  }

  // =========================================================
  // TABLE EXPAND
  // =========================================================

  toggleTable(): void {
    this.isTableExpanded = !this.isTableExpanded;
  }

  // =========================================================
  // PROMPT SUGGESTIONS
  // =========================================================

  onPromptChange(): void {
    const lines = this.promptText.split('\n');

    const currentLine = lines[lines.length - 1].trim().toLowerCase();

    if (!currentLine) {
      this.showAllSuggestions();

      return;
    }

    const matchingSuggestions = this.allSuggestions.filter((suggestion) =>
      suggestion.toLowerCase().includes(currentLine),
    );

    this.filteredSuggestions = [
      ...matchingSuggestions,

      ...this.allSuggestions.filter(
        (suggestion) => !matchingSuggestions.includes(suggestion),
      ),
    ];

    this.showSuggestions = true;
  }

  addSuggestion(text: string): void {
    if (!this.promptText.trim()) {
      this.promptText = text + ' ';
    } else {
      const lines = this.promptText.split('\n');

      const currentLine = lines[lines.length - 1].trim();

      const isPartialText =
        currentLine.length > 0 &&
        !this.selectedSuggestions.includes(currentLine) &&
        this.allSuggestions.some((s) =>
          s.toLowerCase().startsWith(currentLine.toLowerCase()),
        );

      if (isPartialText) {
        lines[lines.length - 1] = text + ' ';
      } else {
        lines.push(text + ' ');
      }

      this.promptText = lines.join('\n');
    }

    if (!this.selectedSuggestions.includes(text)) {
      this.selectedSuggestions.push(text);
    }

    this.showAllSuggestions();
  }

  removeSuggestion(text: string): void {
    this.selectedSuggestions = this.selectedSuggestions.filter(
      (x) => x !== text,
    );

    this.promptText = this.selectedSuggestions.join(', ');
  }

  showAllSuggestions(): void {
    this.filteredSuggestions = [
      ...this.selectedSuggestions,

      ...this.allSuggestions.filter(
        (item) => !this.selectedSuggestions.includes(item),
      ),
    ];

    this.showSuggestions = true;
  }

  // =========================================================
  // CLEAR PROMPT
  // =========================================================

  clearPrompt(): void {
    this.promptText = '';

    this.showSuggestions = true;

    this.showAllSuggestions();
  }

  // =========================================================
  // TYPING RESPONSE
  // =========================================================

  typeResponse(text: string, targetMessage?: ChatMessage): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }

    let index = 0;

    if (targetMessage) {
      targetMessage.displayedContent = '';
      targetMessage.isTyping = true;
    }

    this.typingTimer = setInterval(() => {
      if (index < text.length) {
        const char = text.charAt(index);

        if (targetMessage) {
          targetMessage.displayedContent += char;
        }

        index++;

        this.scrollToBottom();
      } else {
        if (targetMessage) {
          targetMessage.isTyping = false;
        }

        clearInterval(this.typingTimer);

        this.typingTimer = null;
      }
    }, 20);
  }

  // =========================================================
  // VOICE RECOGNITION
  // =========================================================

  initializeSpeechRecognition(): void {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      this.toastr.warning(
        'Voice input is not supported in this browser.',
        'Voice Input',
      );

      return;
    }

    this.recognition = new SpeechRecognition();

    this.recognition.continuous = true;

    this.recognition.interimResults = true;

    this.recognition.lang = 'en-US';

    this.recognition.onstart = () => {
      this.ngZone.run(() => {
        this.isListening = true;

        this.originalPromptText = this.promptText;

        this.finalVoiceText = '';

        this.interimVoiceText = '';
      });
    };

    this.recognition.onresult = (event: any) => {
      let finalText = '';

      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }

      this.ngZone.run(() => {
        if (finalText.trim()) {
          this.finalVoiceText +=
            (this.finalVoiceText ? ' ' : '') + finalText.trim();
        }

        this.interimVoiceText = interimText.trim();

        const liveVoiceText = [this.finalVoiceText, this.interimVoiceText]
          .filter(Boolean)
          .join(' ');

        this.promptText = [this.originalPromptText, liveVoiceText]
          .filter(Boolean)
          .join(' ');

        this.onPromptChange();
      });
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);

      this.ngZone.run(() => {
        if (event.error === 'not-allowed') {
          this.isListening = false;

          this.toastr.warning(
            'Please allow microphone access to use voice input.',
            'Microphone Permission',
          );
        }
      });
    };

    this.recognition.onend = () => {
      this.ngZone.run(() => {
        console.log('Speech recognition ended');
      });
    };
  }

  toggleVoiceInput(): void {
    if (!this.recognition) {
      this.initializeSpeechRecognition();
    }

    if (!this.recognition) {
      return;
    }

    if (!this.isListening) {
      this.originalPromptText = this.promptText;

      this.finalVoiceText = '';

      this.interimVoiceText = '';

      try {
        this.recognition.start();
      } catch (error) {
        console.error('Speech recognition start error:', error);
      }

      return;
    }

    this.recognition.stop();

    this.ngZone.run(() => {
      this.isListening = false;

      const finalText = this.finalVoiceText.trim();

      this.promptText = [this.originalPromptText, finalText]
        .filter(Boolean)
        .join(' ');

      this.onPromptChange();

      this.finalVoiceText = '';

      this.interimVoiceText = '';

      this.originalPromptText = '';
    });
  }

  // =========================================================
  // GET HISTORY FOR API
  // =========================================================

  getHistoryForApi(): any[] {
    return this.chatMessages.map((message) => ({
      role: message.role,
      content: message.content,
    }));
  }

  // =========================================================
  // RESET FORM
  // =========================================================

  resetForm(): void {
    this.newChat();
  }

  // =========================================================
  // CURRENCY CONVERSION
  // =========================================================

  convertCurrency(message: ChatMessage): void {
    const amount = Number(message.currencyAmount);

    if (
      message.currencyAmount === undefined ||
      message.currencyAmount === null ||
      isNaN(amount) ||
      amount < 0
    ) {
      this.toastr.warning('Please enter a valid amount.', 'Currency Converter');
      return;
    }

    if (!message.fromCurrency || !message.toCurrency) {
      this.toastr.warning(
        'Please select both currencies.',
        'Currency Converter',
      );
      return;
    }

    const fromRate = this.currencyRates[message.fromCurrency];
    const toRate = this.currencyRates[message.toCurrency];

    if (fromRate === undefined || toRate === undefined) {
      this.toastr.error(
        'Selected currency rate is unavailable. Try again shortly.',
        'Currency Converter',
      );
      return;
    }

    // Live Conversion Calculation (Base: USD)
    const amountInUsd = amount / fromRate;
    const convertedAmount = amountInUsd * toRate;

    message.currencyResult = Number(convertedAmount.toFixed(2));
    this.saveChatHistory();
  }
  onKeydown(event: KeyboardEvent): void {
    // Check if Enter key was pressed without Shift
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent default newline insertion
      this.onSubmit(); // Submit form
    }
  }
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortTable(message: ChatMessage, column: string): void {
    if (!message.tableData || message.tableData.length === 0) {
      return;
    }

    // Toggle direction when clicking the same column
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    message.tableData = [...message.tableData].sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];

      // Null / empty values go to the bottom
      if (valueA === null || valueA === undefined || valueA === '') {
        return 1;
      }

      if (valueB === null || valueB === undefined || valueB === '') {
        return -1;
      }

      // Numbers
      const numberA = Number(valueA);
      const numberB = Number(valueB);

      if (
        !isNaN(numberA) &&
        !isNaN(numberB) &&
        valueA !== '' &&
        valueB !== ''
      ) {
        return this.sortDirection === 'asc'
          ? numberA - numberB
          : numberB - numberA;
      }

      // Dates
      const dateA = Date.parse(valueA);
      const dateB = Date.parse(valueB);

      if (!isNaN(dateA) && !isNaN(dateB)) {
        return this.sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Strings
      const stringA = String(valueA).toLowerCase();
      const stringB = String(valueB).toLowerCase();

      return this.sortDirection === 'asc'
        ? stringA.localeCompare(stringB)
        : stringB.localeCompare(stringA);
    });
  }
}
